import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../context/auth.store';

// Simple Markdown Formatter for Local Offline Answers
function FormattedMessage({ content }) {
  if (!content) return null;

  const lines = content.split('\n');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13.5px', lineHeight: '1.65' }}>
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={idx} style={{ height: '4px' }} />;

        // Header 3: ### Title
        if (trimmed.startsWith('### ')) {
          return (
            <h4
              key={idx}
              style={{
                fontSize: '15px',
                fontWeight: '800',
                color: '#0F172A',
                margin: '8px 0 2px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {trimmed.replace('### ', '')}
            </h4>
          );
        }

        // Header 2: ## Title
        if (trimmed.startsWith('## ')) {
          return (
            <h3
              key={idx}
              style={{
                fontSize: '16px',
                fontWeight: '900',
                color: '#0F172A',
                margin: '10px 0 4px',
                borderBottom: '1px solid #E2E8F0',
                paddingBottom: '4px',
              }}
            >
              {trimmed.replace('## ', '')}
            </h3>
          );
        }

        // Blockquote / Callout: > text
        if (trimmed.startsWith('> ')) {
          return (
            <div
              key={idx}
              style={{
                background: '#F8FAFC',
                borderLeft: '3px solid #00C0FA',
                padding: '8px 12px',
                borderRadius: '0 8px 8px 0',
                color: '#334155',
                fontSize: '12.5px',
                fontStyle: 'italic',
              }}
            >
              {renderInlineStyles(trimmed.replace('> ', ''))}
            </div>
          );
        }

        // Bullet point: - or *
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          const itemText = trimmed.substring(2);
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', paddingLeft: '4px' }}>
              <span style={{ color: '#00C0FA', fontWeight: 'bold', fontSize: '16px', lineHeight: '18px' }}>•</span>
              <span style={{ flex: 1, color: '#334155' }}>{renderInlineStyles(itemText)}</span>
            </div>
          );
        }

        // Regular paragraph
        return (
          <p key={idx} style={{ margin: 0, color: '#1E293B' }}>
            {renderInlineStyles(line)}
          </p>
        );
      })}
    </div>
  );
}

// Render bold **text**, code `text`, and colored tags
function renderInlineStyles(text) {
  const parts = [];
  let remaining = text;
  let keyIndex = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
    const codeMatch = remaining.match(/`(.*?)`/);

    let firstMatch = null;
    let matchType = null;

    if (boldMatch && (!codeMatch || boldMatch.index < codeMatch.index)) {
      firstMatch = boldMatch;
      matchType = 'bold';
    } else if (codeMatch) {
      firstMatch = codeMatch;
      matchType = 'code';
    }

    if (!firstMatch) {
      parts.push(<span key={keyIndex++}>{remaining}</span>);
      break;
    }

    const matchStart = firstMatch.index;
    if (matchStart > 0) {
      parts.push(<span key={keyIndex++}>{remaining.substring(0, matchStart)}</span>);
    }

    const matchedContent = firstMatch[1];
    if (matchType === 'bold') {
      parts.push(
        <strong key={keyIndex++} style={{ fontWeight: '700', color: '#0F172A' }}>
          {matchedContent}
        </strong>
      );
    } else if (matchType === 'code') {
      const isRiskCritical = /critical/i.test(matchedContent);
      const isRiskHigh = /high/i.test(matchedContent);
      const isRiskLow = /low|clean/i.test(matchedContent);

      let badgeBg = '#F1F5F9';
      let badgeColor = '#0F172A';
      if (isRiskCritical) {
        badgeBg = '#FEE2E2';
        badgeColor = '#991B1B';
      } else if (isRiskHigh) {
        badgeBg = '#FFEDD5';
        badgeColor = '#C2410C';
      } else if (isRiskLow) {
        badgeBg = '#D1FAE5';
        badgeColor = '#065F46';
      }

      parts.push(
        <code
          key={keyIndex++}
          style={{
            background: badgeBg,
            color: badgeColor,
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: "'Space Grotesk', monospace",
            fontWeight: '600',
          }}
        >
          {matchedContent}
        </code>
      );
    }

    remaining = remaining.substring(matchStart + firstMatch[0].length);
  }

  return parts;
}

// Clean markdown and emojis for smooth Text-to-Speech
function cleanTextForSpeech(text) {
  if (!text) return '';
  return text
    .replace(/###\s+/g, '')
    .replace(/##\s+/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/>\s+/g, '')
    .replace(/[-*]\s+/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Visual multi-tier drilldown card for AiAssistantPage
function DrilldownCard({ drilldownData }) {
  const [showRows, setShowRows] = useState(false);
  if (!drilldownData) return null;

  const kpis = drilldownData.kpis || {};
  const aging = drilldownData.agingBuckets || {};
  const branches = drilldownData.topBranches || [];
  const tickets = drilldownData.sampleTickets || [];

  return (
    <div
      style={{
        marginTop: '12px',
        background: '#0F172A',
        border: '1px solid rgba(0, 192, 250, 0.3)',
        borderRadius: '12px',
        padding: '14px',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: '800', color: '#00C0FA', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>📊</span>
          <span>LIVE DRILLDOWN: {drilldownData.reportName}</span>
        </div>
        <span style={{ fontSize: '11px', color: '#94A3B8' }}>
          Sheet: <code style={{ color: '#F8FAFC' }}>{drilldownData.sheetName}</code>
        </span>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
        <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px', borderLeft: '3px solid #00C0FA' }}>
          <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '700' }}>TOTAL TICKETS</div>
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#F8FAFC', marginTop: '2px' }}>
            {kpis.totalRecords?.toLocaleString() || 0}
          </div>
        </div>

        <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px', borderLeft: '3px solid #05CD99' }}>
          <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '700' }}>RESOLUTION RATE</div>
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#05CD99', marginTop: '2px' }}>
            {kpis.resolutionRate || 0}%
          </div>
          <div style={{ fontSize: '9.5px', color: '#94A3B8' }}>{kpis.closedCount} resolved</div>
        </div>

        <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px', borderLeft: '3px solid #F59E0B' }}>
          <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '700' }}>AVG TURNAROUND</div>
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#F59E0B', marginTop: '2px' }}>
            {kpis.averageTat || 'N/A'} <span style={{ fontSize: '11px' }}>days</span>
          </div>
        </div>

        <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px', borderLeft: '3px solid #EF4444' }}>
          <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '700' }}>CRITICAL OVERDUE</div>
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#EF4444', marginTop: '2px' }}>
            {kpis.criticalOverdueCount || 0}
          </div>
          <div style={{ fontSize: '9.5px', color: '#EF4444' }}>>14d SLA breach</div>
        </div>
      </div>

      {/* Aging Distribution */}
      {aging && aging.fresh && (
        <div style={{ marginTop: '2px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#CBD5E1', marginBottom: '4px' }}>
            AGING DISTRIBUTION (PENDING INVENTORY):
          </div>
          <div style={{ display: 'flex', height: '12px', borderRadius: '6px', overflow: 'hidden', background: 'rgba(255, 255, 255, 0.1)' }}>
            <div style={{ width: `${aging.fresh.percentage}%`, background: '#05CD99' }} title={`0-3 Days: ${aging.fresh.count}`} />
            <div style={{ width: `${aging.normal.percentage}%`, background: '#38BDF8' }} title={`4-7 Days: ${aging.normal.count}`} />
            <div style={{ width: `${aging.warning.percentage}%`, background: '#F59E0B' }} title={`8-14 Days: ${aging.warning.count}`} />
            <div style={{ width: `${aging.critical.percentage}%`, background: '#EF4444' }} title={`>14 Days: ${aging.critical.count}`} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#94A3B8', marginTop: '4px' }}>
            <span style={{ color: '#05CD99' }}>● 0-3d ({aging.fresh.count})</span>
            <span style={{ color: '#38BDF8' }}>● 4-7d ({aging.normal.count})</span>
            <span style={{ color: '#F59E0B' }}>● 8-14d ({aging.warning.count})</span>
            <span style={{ color: '#EF4444' }}>● >14d ({aging.critical.count})</span>
          </div>
        </div>
      )}

      {/* Top Branches */}
      {branches.length > 0 && (
        <div style={{ marginTop: '2px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#CBD5E1', marginBottom: '4px' }}>
            TOP SERVICE CENTERS:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {branches.slice(0, 4).map((b, bIdx) => (
              <div key={bIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                <span style={{ width: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#E2E8F0' }}>
                  {b.name}
                </span>
                <div style={{ flex: 1, height: '6px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${b.percentage}%`, height: '100%', background: 'linear-gradient(90deg, #00C0FA, #0052CC)' }} />
                </div>
                <span style={{ width: '55px', textAlign: 'right', color: '#94A3B8', fontSize: '10px' }}>
                  {b.count} ({b.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Row Level Ticket Inspector */}
      {tickets.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          <button
            onClick={() => setShowRows(!showRows)}
            style={{
              background: 'rgba(0, 192, 250, 0.1)',
              border: '1px solid rgba(0, 192, 250, 0.3)',
              borderRadius: '6px',
              padding: '4px 10px',
              color: '#38BDF8',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {showRows ? '▼ Hide Ticket Rows' : `▶ Inspect Normalized Tickets (${tickets.length} records)`}
          </button>

          {showRows && (
            <div
              style={{
                marginTop: '8px',
                maxHeight: '180px',
                overflowY: 'auto',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                fontSize: '10.5px',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: 'rgba(255, 255, 255, 0.08)', color: '#94A3B8' }}>
                    <th style={{ padding: '4px 8px' }}>RMA #</th>
                    <th style={{ padding: '4px 8px' }}>Branch</th>
                    <th style={{ padding: '4px 8px' }}>Status</th>
                    <th style={{ padding: '4px 8px' }}>TAT</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.slice(0, 15).map((t, tIdx) => (
                    <tr key={tIdx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                      <td style={{ padding: '4px 8px', color: '#F8FAFC', fontWeight: '600' }}>{t.rma}</td>
                      <td style={{ padding: '4px 8px', color: '#94A3B8' }}>{t.branch}</td>
                      <td style={{ padding: '4px 8px', color: t.status.toLowerCase().includes('closed') ? '#05CD99' : '#F59E0B' }}>
                        {t.status}
                      </td>
                      <td style={{ padding: '4px 8px', color: t.isOverdue ? '#EF4444' : '#94A3B8' }}>
                        {t.tatDays}d {t.isOverdue && '⚠️'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AiAssistantPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);

  const { user } = useAuthStore();

  // Engine status state
  const [engineStatus, setEngineStatus] = useState(null);
  const [checkingEngine, setCheckingEngine] = useState(false);
  const [preferredEngine, setPreferredEngine] = useState('native');
  const [selectedModel, setSelectedModel] = useState('');
  const [showOllamaInfo, setShowOllamaInfo] = useState(false);

  // Proactive Intelligence & Continuous Dialogue State
  const [proactiveBriefing, setProactiveBriefing] = useState(null);
  const [continuousMode, setContinuousMode] = useState(true); // Continuous follow-up back-and-forth
  const [isFollowUp, setIsFollowUp] = useState(false);
  const followUpTimeoutRef = useRef(null);

  // Voice (STT & TTS) State
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true); // Default enabled for hands-free audio responses
  const [speakingMessageIdx, setSpeakingMessageIdx] = useState(null);
  const recognitionRef = useRef(null);
  const voiceTextRef = useRef('');
  const hasAutoSubmittedRef = useRef(false);
  const autoSubmitTimeoutRef = useRef(null);
  const isVoiceQueryRef = useRef(false);

  // Inspector Panel State (Right panel)
  const [inspectedFile, setInspectedFile] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [riskDetails, setRiskDetails] = useState(null);

  // Trigger hands-free auto-submit when voice finishes
  const triggerVoiceSubmit = (text) => {
    if (!text || !text.trim()) return;
    isVoiceQueryRef.current = true;
    toast.success(`🎙️ Auto-submitting voice: "${text.trim()}"`, { duration: 1600 });
    handleSend(null, text.trim(), true);
  };

  const activeUtteranceRef = useRef(null);

  // Pre-load speech synthesis voices
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
      window.speechSynthesis.getVoices();
    }
  }, []);

  // Unlock audio subsystem on user gesture
  useEffect(() => {
    const unlock = () => {
      try {
        if ('speechSynthesis' in window && window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
      } catch (e) {}
    };
    window.addEventListener('click', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('touchstart', unlock);
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  // Watchdog to prevent long utterance freeze on macOS Chrome
  useEffect(() => {
    let interval = null;
    if (speakingMessageIdx !== null) {
      interval = setInterval(() => {
        if (window.speechSynthesis?.speaking) {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        }
      }, 7500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [speakingMessageIdx]);

  // Subtle audio chime to wake up audio hardware
  const playAudioChime = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const now = ctx.currentTime;
      osc.frequency.setValueAtTime(587.33, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
      osc.start(now);
      osc.stop(now + 0.3);
    } catch (e) {}
  };

  // Proactive Daily Briefing on mount
  useEffect(() => {
    api.get('/ai/proactive-briefing')
      .then((res) => {
        setProactiveBriefing(res.data);
      })
      .catch((err) => console.warn('[AiAssistantPage Briefing]:', err.message));
  }, []);

  // Continuous Follow-up Listening (hands-free dialogue like Alexa/Siri)
  const startFollowUpListening = () => {
    if (!speechSupported || isListening) return;
    setIsFollowUp(true);
    hasAutoSubmittedRef.current = false;
    voiceTextRef.current = '';
    setQuestion('');

    try {
      recognitionRef.current?.start();
      setIsListening(true);

      if (followUpTimeoutRef.current) clearTimeout(followUpTimeoutRef.current);
      followUpTimeoutRef.current = setTimeout(() => {
        if (!hasAutoSubmittedRef.current && (!voiceTextRef.current || !voiceTextRef.current.trim())) {
          try {
            recognitionRef.current?.stop();
          } catch (e) {}
          setIsListening(false);
          setIsFollowUp(false);
        }
      }, 6500);
    } catch (err) {
      console.warn('[KYRO Follow-up Start]:', err);
      setIsListening(false);
      setIsFollowUp(false);
    }
  };

  // Initialize Speech Recognition with hands-free auto-submit
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
          setIsListening(true);
          hasAutoSubmittedRef.current = false;
          voiceTextRef.current = '';
        };

        recognition.onresult = (event) => {
          if (followUpTimeoutRef.current) clearTimeout(followUpTimeoutRef.current);
          let interimTranscript = '';
          let finalTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              finalTranscript += event.results[i][0].transcript;
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }
          const current = (finalTranscript || interimTranscript).trim();
          if (current) {
            setQuestion(current);
            voiceTextRef.current = current;
          }

          // Auto-submit when user finishes speaking (short natural pause)
          if (finalTranscript && finalTranscript.trim().length > 0) {
            if (autoSubmitTimeoutRef.current) clearTimeout(autoSubmitTimeoutRef.current);
            autoSubmitTimeoutRef.current = setTimeout(() => {
              if (!hasAutoSubmittedRef.current) {
                hasAutoSubmittedRef.current = true;
                try {
                  recognitionRef.current?.stop();
                } catch (e) {}
                setIsListening(false);
                setIsFollowUp(false);
                triggerVoiceSubmit(finalTranscript.trim());
              }
            }, 600);
          }
        };

        recognition.onerror = (event) => {
          console.warn('[KYRO Voice Error]:', event.error);
          setIsListening(false);
          setIsFollowUp(false);
          if (event.error === 'not-allowed') {
            toast.error('Microphone access blocked. Please permit mic access in your browser.');
          } else if (event.error !== 'no-speech') {
            toast.error(`Voice input error: ${event.error}`);
          }
        };

        recognition.onend = () => {
          setIsListening(false);
          setIsFollowUp(false);
          // Fallback auto-submit on speech pause/end if not already submitted
          if (!hasAutoSubmittedRef.current && voiceTextRef.current && voiceTextRef.current.trim().length > 0) {
            hasAutoSubmittedRef.current = true;
            const textToSubmit = voiceTextRef.current.trim();
            voiceTextRef.current = '';
            triggerVoiceSubmit(textToSubmit);
          }
        };

        recognitionRef.current = recognition;
      } catch (e) {
        console.warn('SpeechRecognition init error:', e);
      }
    }
  }, []);

  // Speak Text Function (TTS)
  const speakMessage = (text, messageIdx) => {
    if (!('speechSynthesis' in window)) {
      toast.error('Speech synthesis not supported in this browser.');
      return;
    }

    if (speakingMessageIdx === messageIdx) {
      activeUtteranceRef.current = null;
      window.__kyroUtterance = null;
      window.speechSynthesis.cancel();
      setSpeakingMessageIdx(null);
      return;
    }

    // Play subtle chime to wake audio hardware
    playAudioChime();

    // 1. Cancel previous speech if active
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }

    const cleanSpeech = cleanTextForSpeech(text) || text;
    if (!cleanSpeech) return;

    // 2. CRUCIAL FIX FOR CHROME/EDGE: Give audio subsystem 65ms to reset after cancel before speak()
    setTimeout(() => {
      try {
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }

        const utterance = new SpeechSynthesisUtterance(cleanSpeech);
        // CRITICAL BUG FIX: Store in ref and window to prevent V8 garbage collection!
        activeUtteranceRef.current = utterance;
        window.__kyroUtterance = utterance;

        utterance.lang = 'en-US';
        utterance.volume = 1.0;
        utterance.rate = 1.05;
        utterance.pitch = 0.95;

        const voices = window.speechSynthesis.getVoices();
        if (voices && voices.length > 0) {
          const kyroVoice =
            voices.find(
              (v) =>
                v.lang.startsWith('en') &&
                (v.name.includes('Daniel') ||
                  v.name.includes('UK English') ||
                  v.name.includes('Male') ||
                  v.name.includes('Natural') ||
                  v.name.includes('Google UK English Male') ||
                  v.name.includes('Alex'))
            ) || voices.find((v) => v.lang.startsWith('en'));

          if (kyroVoice) utterance.voice = kyroVoice;
        }

        utterance.onstart = () => {
          setSpeakingMessageIdx(messageIdx);
          setIsFollowUp(false);
        };

        utterance.onend = () => {
          setSpeakingMessageIdx(null);
          activeUtteranceRef.current = null;
          window.__kyroUtterance = null;
          // Auto continuous follow-up listening mode
          if (continuousMode && speechSupported) {
            setTimeout(() => {
              startFollowUpListening();
            }, 350);
          }
        };

        utterance.onerror = (e) => {
          console.warn('[KYRO speakMessage Error]:', e);
          setSpeakingMessageIdx(null);
          setIsFollowUp(false);
          activeUtteranceRef.current = null;
          window.__kyroUtterance = null;
        };

        window.speechSynthesis.resume?.();
        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.error('[KYRO speakMessage exception]:', err);
        setSpeakingMessageIdx(null);
      }
    }, 65);
  };

  const toggleVoiceInput = () => {
    if (!speechSupported) {
      toast.error('Speech recognition not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      if (autoSubmitTimeoutRef.current) clearTimeout(autoSubmitTimeoutRef.current);
      try {
        recognitionRef.current?.stop();
      } catch (e) {}
      setIsListening(false);
      if (voiceTextRef.current && voiceTextRef.current.trim().length > 0 && !hasAutoSubmittedRef.current) {
        hasAutoSubmittedRef.current = true;
        triggerVoiceSubmit(voiceTextRef.current.trim());
      }
    } else {
      window.speechSynthesis?.cancel();
      setSpeakingMessageIdx(null);
      setQuestion('');
      voiceTextRef.current = '';
      hasAutoSubmittedRef.current = false;
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        toast('🎙️ KYRO is listening... Speak your question now', { icon: '🎙️' });
      } catch (err) {
        console.warn(err);
        setIsListening(false);
      }
    }
  };

  // Fetch local engine status
  const fetchEngineStatus = async () => {
    setCheckingEngine(true);
    try {
      const res = await api.get('/ai/engine-status');
      setEngineStatus(res.data);
      if (res.data.ollama?.available) {
        setPreferredEngine('ollama');
        if (res.data.ollama.models?.length > 0) {
          setSelectedModel(res.data.ollama.models[0]);
        }
      } else {
        setPreferredEngine('native');
      }
    } catch (err) {
      console.error('[Engine Status Error]:', err);
    } finally {
      setCheckingEngine(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await api.get('/ai/conversations');
      setConversations(res.data);
      if (res.data.length > 0 && !activeConv) {
        selectConversation(res.data[0]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load chat history');
    } finally {
      setLoadingChats(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchEngineStatus();
  }, []);

  const selectConversation = async (conv) => {
    setActiveConv(conv);
    setMessages([]);
    window.speechSynthesis?.cancel();
    setSpeakingMessageIdx(null);
    try {
      const res = await api.get(`/ai/conversations/${conv.id}`);
      setMessages(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load messages');
    }
  };

  const handleCreateChat = () => {
    window.speechSynthesis?.cancel();
    setSpeakingMessageIdx(null);
    setActiveConv(null);
    setMessages([]);
    setQuestion('');
  };

  const handleSend = async (e, textOverride, isVoice = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const promptToSend = (typeof textOverride === 'string' ? textOverride : question).trim();
    if (!promptToSend || sending) return;

    if (autoSubmitTimeoutRef.current) clearTimeout(autoSubmitTimeoutRef.current);
    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch (err) {}
      setIsListening(false);
    }

    const wasVoice = isVoice || isVoiceQueryRef.current;
    isVoiceQueryRef.current = false;

    const userMsg = { role: 'user', content: promptToSend };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);
    setQuestion('');

    try {
      const res = await api.post('/ai/chat', {
        question: promptToSend,
        conversationId: activeConv?.id || null,
        preferredEngine,
        preferredModel: selectedModel,
      });

      const newAssistantMsg = {
        role: 'assistant',
        content: res.data.answer,
        voiceSummary: res.data.voiceSummary || '',
        navigateTo: res.data.navigateTo || null,
        drilldownData: res.data.drilldownData || null,
        sources: JSON.stringify(res.data.sources || []),
        engine: res.data.engine,
        modelUsed: res.data.modelUsed,
        latencyMs: res.data.latencyMs,
        airGapped: res.data.airGapped,
      };

      setMessages((prev) => {
        const nextMsgs = [...prev, newAssistantMsg];
        // Automatically respond in audio out loud without click of listen button
        if (ttsEnabled !== false) {
          const textToSpeak = res.data.voiceSummary || res.data.answer;
          setTimeout(() => {
            speakMessage(textToSpeak, nextMsgs.length - 1);
          }, 150);
        }
        return nextMsgs;
      });

      fetchConversations();
      if (!activeConv && res.data.conversationId) {
        setActiveConv({ id: res.data.conversationId, title: promptToSend.substring(0, 36) });
      }
    } catch (err) {
      console.error(err);
      toast.error('KYRO offline assistant query failed. Please retry.');
    } finally {
      setSending(false);
    }
  };

  const inspectCitation = async (source) => {
    setInspectedFile(source);
    setLoadingPermissions(true);
    try {
      const [permRes, riskRes] = await Promise.all([
        api.get(`/permissions/file/${source.fileId}`),
        api.get(`/permissions/file/${source.fileId}/risk`),
      ]);
      setPermissions(permRes.data);
      setRiskDetails(riskRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Could not load permission analytics for cited file');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const triggerPrompt = (promptText) => {
    setQuestion(promptText);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 75px)',
        padding: '16px 24px',
        gap: '14px',
        background: '#F4F7FE',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Top HUD Status Bar ── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0B132B 0%, #1C2541 100%)',
          borderRadius: '16px',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white',
          boxShadow: '0 8px 24px rgba(11, 19, 43, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'radial-gradient(circle, #00C0FA 0%, #0052CC 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 16px rgba(0, 192, 250, 0.4)',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="4" />
              <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
            </svg>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '900', fontSize: '16px', letterSpacing: '0.04em' }}>
                KYRO <span style={{ color: '#00C0FA', fontSize: '13px', fontWeight: '700' }}>// AI CORE</span>
              </span>
              <span
                style={{
                  background: 'rgba(5, 205, 153, 0.18)',
                  color: '#05CD99',
                  border: '1px solid rgba(5, 205, 153, 0.4)',
                  fontSize: '10px',
                  fontWeight: '800',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  letterSpacing: '0.04em',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#05CD99',
                    boxShadow: '0 0 8px #05CD99',
                    display: 'inline-block',
                  }}
                />
                100% AIR-GAPPED OFFLINE
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>
              Voice-Activated Document & M365 Compliance Intelligence · Zero Cloud Data Egress
            </div>
          </div>
        </div>

        {/* Engine Controls, Voice TTS Toggle & Stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Catalog Volume Indicator */}
          {engineStatus && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.06)',
                padding: '6px 12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                fontSize: '11.5px',
              }}
            >
              <span style={{ color: '#00C0FA', fontWeight: '800' }}>⚡ Indexed:</span>
              <span style={{ color: '#E2E8F0' }}>
                {engineStatus.nativeEngine?.filesIndexed?.toLocaleString() || '57,654'} Files ·{' '}
                {engineStatus.nativeEngine?.reportsIndexed?.toLocaleString() || '103,825'} Reports
              </span>
            </div>
          )}

          {/* KYRO Voice Output Toggle */}
          <button
            onClick={() => {
              const next = !ttsEnabled;
              setTtsEnabled(next);
              if (!next) {
                window.speechSynthesis?.cancel();
                setSpeakingMessageIdx(null);
              }
              toast(next ? '🔊 KYRO Voice Output: ENABLED' : '🔇 KYRO Voice Output: MUTED', {
                icon: next ? '🔊' : '🔇',
              });
            }}
            style={{
              background: ttsEnabled ? 'rgba(0, 192, 250, 0.25)' : 'rgba(255, 255, 255, 0.06)',
              border: ttsEnabled ? '1px solid #00C0FA' : '1px solid rgba(255, 255, 255, 0.1)',
              color: ttsEnabled ? '#00C0FA' : '#94A3B8',
              padding: '6px 12px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s',
            }}
            title="Toggle whether KYRO automatically reads out responses aloud"
          >
            <span>{ttsEnabled ? '🔊' : '🔇'}</span>
            <span>Voice Mode: {ttsEnabled ? 'ON' : 'OFF'}</span>
          </button>

          {/* Continuous Follow-up Mode Toggle */}
          <button
            onClick={() => {
              const next = !continuousMode;
              setContinuousMode(next);
              toast(next ? '🎙️ Continuous Follow-up: ENABLED' : '🎙️ Continuous Follow-up: DISABLED', {
                icon: next ? '🎙️' : '⏹️',
              });
            }}
            style={{
              background: continuousMode ? 'rgba(5, 205, 153, 0.25)' : 'rgba(255, 255, 255, 0.06)',
              border: continuousMode ? '1px solid #05CD99' : '1px solid rgba(255, 255, 255, 0.1)',
              color: continuousMode ? '#05CD99' : '#94A3B8',
              padding: '6px 12px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s',
            }}
            title="When ON, KYRO automatically listens for your follow-up reply after answering aloud (like Alexa/Siri)"
          >
            <span>{continuousMode ? '🎙️ Follow-up: ON' : '🎙️ Follow-up: OFF'}</span>
          </button>

          {/* Engine Selector */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.25)',
              borderRadius: '10px',
              padding: '3px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <button
              onClick={() => setPreferredEngine('native')}
              style={{
                background: preferredEngine === 'native' ? '#FF6B00' : 'transparent',
                color: 'white',
                border: 'none',
                padding: '5px 12px',
                borderRadius: '8px',
                fontSize: '11.5px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              title="Runs native PostgreSQL search, permission resolution & security audits"
            >
              🛡️ Native Engine
            </button>

            <button
              onClick={() => {
                if (engineStatus?.ollama?.available) {
                  setPreferredEngine('ollama');
                } else {
                  setShowOllamaInfo(true);
                }
              }}
              style={{
                background: preferredEngine === 'ollama' ? '#00C0FA' : 'transparent',
                color: engineStatus?.ollama?.available ? 'white' : '#64748B',
                border: 'none',
                padding: '5px 12px',
                borderRadius: '8px',
                fontSize: '11.5px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
              title={
                engineStatus?.ollama?.available
                  ? 'Local Neural LLM synthesis via Ollama'
                  : 'Ollama not detected on port 11434. Click to view setup instructions.'
              }
            >
              <span>🦙 Ollama Local</span>
              {!engineStatus?.ollama?.available && (
                <span
                  style={{
                    fontSize: '9px',
                    background: 'rgba(255,255,255,0.15)',
                    padding: '1px 5px',
                    borderRadius: '4px',
                    color: '#CBD5E1',
                  }}
                >
                  Standby
                </span>
              )}
            </button>
          </div>

          {/* If Ollama has models, allow selection */}
          {preferredEngine === 'ollama' && engineStatus?.ollama?.models?.length > 0 && (
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                borderRadius: '8px',
                padding: '5px 10px',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {engineStatus.ollama.models.map((m) => (
                <option key={m} value={m} style={{ background: '#0B132B', color: 'white' }}>
                  {m}
                </option>
              ))}
            </select>
          )}

          {/* Refresh Engine Status */}
          <button
            onClick={fetchEngineStatus}
            disabled={checkingEngine}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              color: '#CBD5E1',
              borderRadius: '8px',
              padding: '6px 10px',
              fontSize: '12px',
              cursor: checkingEngine ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Scan localhost:11434 for active Ollama LLMs"
          >
            <span style={{ display: 'inline-block', transform: checkingEngine ? 'rotate(180deg)' : 'none', transition: 'transform 0.4s' }}>
              🔄
            </span>
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Ping</span>
          </button>
        </div>
      </div>

      {/* ── Main Workspace: 3 Columns ── */}
      <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
        {/* ── Left Column: Audit Sessions & Integrity Badge ── */}
        <div
          style={{
            width: '260px',
            minWidth: '260px',
            background: 'white',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            boxShadow: '0 4px 16px rgba(112, 144, 176, 0.06)',
          }}
        >
          <button
            onClick={handleCreateChat}
            style={{
              background: 'linear-gradient(135deg, #FF6B00 0%, #FF3D00 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '11px 16px',
              fontWeight: '700',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(255, 107, 0, 0.25)',
              marginBottom: '16px',
              transition: 'all 0.15s',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            + New Audit Session
          </button>

          <div
            style={{
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#94A3B8',
              fontWeight: '800',
              marginBottom: '10px',
              paddingLeft: '4px',
            }}
          >
            Audit History
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {conversations.map((conv) => {
              const isSelected = activeConv?.id === conv.id;
              return (
                <div
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: isSelected ? '700' : '500',
                    background: isSelected ? '#FFF5F0' : 'transparent',
                    color: isSelected ? '#FF6B00' : '#475569',
                    border: isSelected ? '1px solid #FFD8CC' : '1px solid transparent',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  title={conv.title}
                >
                  <span style={{ opacity: isSelected ? 1 : 0.4 }}>💬</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{conv.title}</span>
                </div>
              );
            })}
            {!loadingChats && conversations.length === 0 && (
              <div style={{ fontSize: '12px', color: '#94A3B8', textAlign: 'center', padding: '30px 10px' }}>
                No prior audit sessions.
              </div>
            )}
          </div>

          {/* Voice Input Capabilities Status Pill */}
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              background: '#F8FAFC',
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              fontSize: '11px',
              color: '#64748B',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: '#0F172A', marginBottom: '4px' }}>
              <span>🎙️</span> Voice Engine Ready
            </div>
            Speech-to-Text and Text-to-Speech active. Click the microphone or use Voice Mode to interact naturally with KYRO.
          </div>
        </div>

        {/* ── Middle Column: Chat & Intelligence Canvas ── */}
        <div
          style={{
            flex: 1,
            background: 'white',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(112, 144, 176, 0.06)',
          }}
        >
          {/* Messages Stream */}
          <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {messages.length === 0 && (
              <div style={{ margin: 'auto', maxWidth: '640px', textAlign: 'center', padding: '20px 0' }}>
                <div
                  style={{
                    width: '68px',
                    height: '68px',
                    borderRadius: '20px',
                    background: 'radial-gradient(circle, #FF6B00 0%, #FF3D00 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    boxShadow: '0 8px 24px rgba(255, 107, 0, 0.25)',
                  }}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0F172A', margin: '0 0 8px' }}>
                  KYRO Autonomous Document Intelligence
                </h3>
                <p style={{ color: '#64748B', fontSize: '13.5px', lineHeight: '1.6', margin: '0 0 20px' }}>
                  Speak or type questions across <strong>57,654</strong> indexed enterprise files and <strong>103,825</strong> reports.
                  Every query resolves tenant access control, permission inheritance chains, and security risk indicators locally.
                </p>

                {/* Proactive Operations Digest Card */}
                {proactiveBriefing && (
                  <div
                    style={{
                      marginBottom: '20px',
                      padding: '16px 20px',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #0B132B 0%, #1C2541 100%)',
                      color: 'white',
                      textAlign: 'left',
                      border: '1px solid rgba(0, 192, 250, 0.35)',
                      boxShadow: '0 8px 24px rgba(11, 19, 43, 0.25), 0 0 16px rgba(0, 192, 250, 0.1)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>⚡</span>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '800', color: '#00C0FA' }}>
                            {proactiveBriefing.greeting || 'KYRO Proactive Operations Brief'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                            Proactive summary based on your mapped email flows & enterprise activity
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          handleSend(null, 'What is my proactive daily briefing?');
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #00C0FA 0%, #0088CC 100%)',
                          color: '#0B132B',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '6px 14px',
                          fontSize: '11.5px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 8px rgba(0, 192, 250, 0.4)',
                        }}
                      >
                        <span>🔊 Play Full Brief</span>
                      </button>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '12px',
                        marginTop: '12px',
                        padding: '12px 14px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '10px',
                        fontSize: '12px',
                      }}
                    >
                      <div>
                        <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>YOUR EMAIL FLOWS</div>
                        <div style={{ color: '#00C0FA', fontWeight: '800', fontSize: '16px' }}>
                          {proactiveBriefing.stats?.mappedFlowsCount ?? 0} Mapped
                        </div>
                      </div>
                      <div>
                        <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>ENTERPRISE REPORTS</div>
                        <div style={{ color: '#FFF', fontWeight: '800', fontSize: '16px' }}>
                          {(proactiveBriefing.stats?.totalReports || 103825).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>INDEXED M365 FILES</div>
                        <div style={{ color: '#FFF', fontWeight: '800', fontSize: '16px' }}>
                          {(proactiveBriefing.stats?.totalFiles || 57654).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>SYSTEM INTEGRITY</div>
                        <div style={{ color: '#05CD99', fontWeight: '800', fontSize: '16px' }}>
                          100% Offline
                        </div>
                      </div>
                    </div>

                    {proactiveBriefing.quickActions && proactiveBriefing.quickActions.length > 0 && (
                      <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {proactiveBriefing.quickActions.map((qa, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSend(null, qa.replace(/^[^\w]+/, '').trim())}
                            style={{
                              background: 'rgba(255, 255, 255, 0.08)',
                              border: '1px solid rgba(255, 255, 255, 0.18)',
                              borderRadius: '20px',
                              color: '#CBD5E1',
                              fontSize: '11px',
                              padding: '5px 12px',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {qa}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Prompts Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', textAlign: 'left' }}>
                  <button
                    onClick={() => triggerPrompt('Show documents with high security risk exposure')}
                    style={{
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '12.5px',
                      color: '#1E293B',
                      fontWeight: '600',
                      transition: 'all 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <span style={{ color: '#EE5D50', fontSize: '11px', fontWeight: '800' }}>⚠️ EXPOSURE SCAN</span>
                    "Show documents with high security risk exposure"
                  </button>

                  <button
                    onClick={() => triggerPrompt('Who has access to the confidential files?')}
                    style={{
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '12.5px',
                      color: '#1E293B',
                      fontWeight: '600',
                      transition: 'all 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <span style={{ color: '#0052CC', fontSize: '11px', fontWeight: '800' }}>👥 ACCESS AUDIT</span>
                    "Who has access to the confidential files?"
                  </button>

                  <button
                    onClick={() => triggerPrompt('Check confidential salary or payroll documents')}
                    style={{
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '12.5px',
                      color: '#1E293B',
                      fontWeight: '600',
                      transition: 'all 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <span style={{ color: '#FF6B00', fontSize: '11px', fontWeight: '800' }}>💼 COMPENSATION</span>
                    "Check confidential salary or payroll documents"
                  </button>

                  <button
                    onClick={() => triggerPrompt('Find central operations reports for dispatch and RMA')}
                    style={{
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '12.5px',
                      color: '#1E293B',
                      fontWeight: '600',
                      transition: 'all 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <span style={{ color: '#05CD99', fontSize: '11px', fontWeight: '800' }}>📊 REPORTS INTELLIGENCE</span>
                    "Find central operations reports for dispatch and RMA"
                  </button>
                </div>
              </div>
            )}

            {messages.map((msg, idx) => {
              const isUser = msg.role === 'user';
              const isSpeaking = speakingMessageIdx === idx;
              let parsedSources = [];
              try {
                if (msg.sources) {
                  parsedSources = typeof msg.sources === 'string' ? JSON.parse(msg.sources) : msg.sources;
                }
              } catch (e) {
                parsedSources = [];
              }

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: isUser ? 'flex-end' : 'flex-start',
                    maxWidth: isUser ? '75%' : '88%',
                    gap: '6px',
                  }}
                >
                  {/* Message Card */}
                  <div
                    style={{
                      padding: isUser ? '12px 18px' : '16px 20px',
                      borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: isUser ? 'linear-gradient(135deg, #0B132B 0%, #1C2541 100%)' : '#FFFFFF',
                      color: isUser ? '#FFFFFF' : '#1E293B',
                      border: isUser ? 'none' : '1px solid #E2E8F0',
                      boxShadow: isUser ? '0 4px 14px rgba(11, 19, 43, 0.15)' : '0 2px 8px rgba(0,0,0,0.03)',
                    }}
                  >
                    {!isUser && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '10px',
                          borderBottom: '1px solid #F1F5F9',
                          paddingBottom: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '6px',
                              background: '#00C0FA',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              color: 'white',
                            }}
                          >
                            K
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: '800', color: '#0F172A' }}>
                            KYRO AI Response
                          </span>
                          <span
                            style={{
                              fontSize: '10px',
                              background: '#E6FAF5',
                              color: '#05CD99',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '700',
                            }}
                          >
                            🔒 Air-Gapped
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {/* Speak Summary Button */}
                          <button
                            onClick={() => speakMessage(msg.voiceSummary || msg.content, idx)}
                            style={{
                              background: isSpeaking ? '#EFF6FF' : '#F8FAFC',
                              border: isSpeaking ? '1px solid #3B82F6' : '1px solid #E2E8F0',
                              borderRadius: '6px',
                              padding: '3px 8px',
                              fontSize: '11px',
                              color: isSpeaking ? '#1D4ED8' : '#475569',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: '700',
                            }}
                            title={isSpeaking ? 'Stop voice' : 'Replay KYRO audio response'}
                          >
                            <span>{isSpeaking ? '⏹️' : '🔊'}</span>
                            <span>{isSpeaking ? 'Stop Audio' : 'Replay Audio'}</span>
                          </button>

                          {msg.latencyMs && (
                            <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: '600' }}>
                              ⚡ {msg.latencyMs}ms
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {!isUser && msg.voiceSummary && (
                      <div
                        style={{
                          background: 'rgba(0, 192, 250, 0.08)',
                          borderLeft: '3px solid #00C0FA',
                          padding: '8px 12px',
                          borderRadius: '0 8px 8px 0',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                        }}
                      >
                        <div style={{ fontSize: '12.5px', color: '#0F172A', fontWeight: '600', fontStyle: 'italic' }}>
                          🎙️ <strong style={{ fontStyle: 'normal', color: '#0052CC' }}>Audio Summary:</strong> "{msg.voiceSummary}"
                        </div>
                      </div>
                    )}

                    {isUser ? (
                      <div style={{ fontSize: '13.5px', lineHeight: '1.6' }}>{msg.content}</div>
                    ) : (
                      <FormattedMessage content={msg.content} />
                    )}

                    {!isUser && msg.navigateTo && (
                      <div style={{ marginTop: '10px' }}>
                        <a
                          href={msg.navigateTo}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'linear-gradient(135deg, #00C0FA 0%, #0052CC 100%)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            textDecoration: 'none',
                          }}
                        >
                          <span>🚀</span> Go to {msg.navigateTo} &rarr;
                        </a>
                      </div>
                    )}

                    {!isUser && msg.drilldownData && (
                      <DrilldownCard drilldownData={msg.drilldownData} />
                    )}
                  </div>

                  {/* Sources Citation Bar */}
                  {!isUser && parsedSources.length > 0 && (
                    <div
                      style={{
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        padding: '10px 14px',
                        marginTop: '4px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          fontWeight: '800',
                          color: '#64748B',
                          marginBottom: '8px',
                          letterSpacing: '0.04em',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span>📂</span> Cited Documents ({parsedSources.length}) · Click to Audit in Right Inspector
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {parsedSources.map((source, sIdx) => {
                          const isRiskHigh = source.riskScore >= 40;
                          return (
                            <button
                              key={sIdx}
                              onClick={() => inspectCitation(source)}
                              style={{
                                background: inspectedFile?.fileId === source.fileId ? '#EFF6FF' : 'white',
                                border:
                                  inspectedFile?.fileId === source.fileId
                                    ? '1.5px solid #0052CC'
                                    : '1px solid #CBD5E1',
                                borderRadius: '8px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                color: '#1E293B',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.15s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                              }}
                              title="Click to view live permission pathways & risk details"
                            >
                              <span>📄</span>
                              <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {source.name}
                              </span>
                              <span
                                style={{
                                  fontSize: '10px',
                                  padding: '1px 5px',
                                  borderRadius: '4px',
                                  background: isRiskHigh ? '#FEE2E2' : '#D1FAE5',
                                  color: isRiskHigh ? '#991B1B' : '#065F46',
                                  fontWeight: '700',
                                }}
                              >
                                {source.riskScore}/100
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {sending && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#FFFFFF',
                  padding: '12px 18px',
                  borderRadius: '16px',
                  border: '1px solid #E2E8F0',
                  width: 'fit-content',
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: '2px solid #00C0FA',
                    borderTopColor: 'transparent',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>
                  KYRO resolving permission matrix & offline intelligence...
                </span>
              </div>
            )}
          </div>

          {/* Audio Wave / Listening Banner */}
          {isListening && (
            <div
              style={{
                background: isFollowUp
                  ? 'linear-gradient(90deg, #0B132B 0%, #064E3B 100%)'
                  : 'linear-gradient(90deg, #0B132B 0%, #1E293B 100%)',
                color: isFollowUp ? '#05CD99' : '#00C0FA',
                padding: '8px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: isFollowUp ? '1px solid #05CD99' : '1px solid #00C0FA',
                fontSize: '12px',
                fontWeight: '700',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isFollowUp ? '#05CD99' : '#EE5D50',
                    boxShadow: isFollowUp ? '0 0 10px #05CD99' : '0 0 10px #EE5D50',
                    animation: 'pulse 1s infinite',
                  }}
                />
                <span>
                  {isFollowUp
                    ? '🎙️ Continuous Follow-up Active · Speak anytime (or say "thank you" / "done" to exit)'
                    : '🎙️ KYRO Voice Active · Speak your question (will auto-submit on pause & auto-reply aloud)'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: isFollowUp ? '#6EE7B7' : '#38BDF8', marginRight: '6px' }}>
                  {isFollowUp ? 'Follow-Up Window' : 'Hands-Free'}
                </span>
                <span style={{ height: '14px', width: '3px', background: isFollowUp ? '#05CD99' : '#00C0FA', borderRadius: '2px' }} />
                <span style={{ height: '20px', width: '3px', background: isFollowUp ? '#05CD99' : '#00C0FA', borderRadius: '2px' }} />
                <span style={{ height: '10px', width: '3px', background: isFollowUp ? '#05CD99' : '#00C0FA', borderRadius: '2px' }} />
                <span style={{ height: '16px', width: '3px', background: isFollowUp ? '#05CD99' : '#00C0FA', borderRadius: '2px' }} />
              </div>
            </div>
          )}

          {/* Input Bar with Voice & Text Send */}
          <form
            onSubmit={handleSend}
            style={{
              padding: '14px 20px',
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              gap: '10px',
              background: '#FFFFFF',
              alignItems: 'center',
            }}
          >
            {/* Voice Input Microphone Button */}
            <button
              type="button"
              onClick={toggleVoiceInput}
              style={{
                background: isListening
                  ? 'radial-gradient(circle, #EE5D50 0%, #B91C1C 100%)'
                  : speechSupported
                  ? '#F1F5F9'
                  : '#F8FAFC',
                color: isListening ? 'white' : speechSupported ? '#0F172A' : '#94A3B8',
                border: isListening ? '2px solid #FECACA' : '1.5px solid #CBD5E1',
                borderRadius: '12px',
                width: '46px',
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: speechSupported ? 'pointer' : 'not-allowed',
                boxShadow: isListening ? '0 0 14px rgba(238, 93, 80, 0.5)' : 'none',
                transition: 'all 0.2s',
              }}
              title={
                isListening
                  ? 'Click to stop listening'
                  : speechSupported
                  ? 'Click to speak to KYRO (Hands-free Voice Input)'
                  : 'Speech recognition not supported in this browser'
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>

            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={
                isListening
                  ? '🎙️ Listening... Speak your question (KYRO will auto-submit & auto-reply)'
                  : 'Ask KYRO or speak about reports, email flows, user mappings, risks...'
              }
              style={{
                flex: 1,
                border: isListening ? '2px solid #00C0FA' : '1.5px solid #CBD5E1',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '13.5px',
                outline: 'none',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#00C0FA')}
              onBlur={(e) => (e.target.style.borderColor = '#CBD5E1')}
            />

            {/* Text Send Button */}
            <button
              type="submit"
              disabled={sending || !question.trim()}
              style={{
                background: sending || !question.trim() ? '#CBD5E1' : 'linear-gradient(135deg, #00C0FA 0%, #0052CC 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                width: '46px',
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: sending || !question.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: sending || !question.trim() ? 'none' : '0 4px 12px rgba(0, 192, 250, 0.3)',
              }}
              title="Send to KYRO"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="22 2 11 13 2 9 22 2" />
                <line x1="22" y1="2" x2="11" y2="13" />
              </svg>
            </button>
          </form>
        </div>

        {/* ── Right Column: Interactive Audit Inspector ── */}
        <div
          style={{
            width: '380px',
            minWidth: '380px',
            background: 'white',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(112, 144, 176, 0.06)',
          }}
        >
          {inspectedFile ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <span
                  style={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    color: '#0052CC',
                    fontWeight: '800',
                    letterSpacing: '0.04em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>🔍</span> Live Audit Inspector
                </span>
                <button
                  onClick={() => setInspectedFile(null)}
                  style={{
                    background: '#F1F5F9',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#64748B',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  &times;
                </button>
              </div>

              <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0F172A', margin: '0 0 6px', wordBreak: 'break-word' }}>
                {inspectedFile.name}
              </h3>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span
                  style={{
                    background: '#F1F5F9',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#475569',
                  }}
                >
                  {inspectedFile.classification || 'Internal'}
                </span>
                {inspectedFile.webUrl && (
                  <a
                    href={inspectedFile.webUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '12px', color: '#0052CC', textDecoration: 'none', fontWeight: '700' }}
                  >
                    Open M365 &rarr;
                  </a>
                )}
              </div>

              {/* Quick Action Button */}
              <button
                onClick={() => {
                  setQuestion(`Who has access to "${inspectedFile.name}" and what are the security risks?`);
                }}
                style={{
                  width: '100%',
                  background: '#F0F9FF',
                  border: '1px solid #BAE6FD',
                  borderRadius: '10px',
                  padding: '9px 12px',
                  color: '#0284C7',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginBottom: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <span>⚡</span> Ask KYRO to Audit This File
              </button>

              <hr style={{ margin: '0 0 16px', border: '0', borderTop: '1px solid #E2E8F0' }} />

              {/* Risk Details Card */}
              {riskDetails && (
                <div style={{ marginBottom: '20px' }}>
                  <div
                    style={{
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      color: '#64748B',
                      fontWeight: '800',
                      marginBottom: '8px',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Security Exposure Score
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: riskDetails.riskLevel === 'Critical' ? '#FEF2F2' : riskDetails.riskLevel === 'High' ? '#FFF7ED' : '#F0FDF4',
                      border:
                        riskDetails.riskLevel === 'Critical'
                          ? '1px solid #FECACA'
                          : riskDetails.riskLevel === 'High'
                          ? '1px solid #FED7AA'
                          : '1px solid #BBF7D0',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      marginBottom: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: '800',
                        fontSize: '13px',
                        color: riskDetails.riskLevel === 'Critical' ? '#991B1B' : riskDetails.riskLevel === 'High' ? '#C2410C' : '#15803D',
                      }}
                    >
                      {riskDetails.riskLevel.toUpperCase()} RISK
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '800', color: '#1E293B' }}>
                      {riskDetails.riskScore} / 100
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {riskDetails.flags?.map((flag, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: '#FFFBEB',
                          border: '1px solid #FDE68A',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          color: '#92400E',
                          fontWeight: '600',
                        }}
                      >
                        ⚠️ {flag}
                      </div>
                    ))}
                    {riskDetails.flags?.length === 0 && (
                      <div style={{ fontSize: '12.5px', color: '#05CD99', fontWeight: '600' }}>
                        ✓ No external or anonymous exposure anomalies found.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Who Has Access Breakdown */}
              <div>
                <div
                  style={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    color: '#64748B',
                    fontWeight: '800',
                    marginBottom: '10px',
                    letterSpacing: '0.04em',
                  }}
                >
                  Authorized Principals ({permissions.length})
                </div>

                {loadingPermissions ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '2.5px solid #00C0FA',
                        borderTopColor: 'transparent',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 8px',
                      }}
                    />
                    <div style={{ fontSize: '12px', color: '#64748B' }}>Traversing permission hierarchy...</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {permissions.map((perm, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '10px',
                          background: '#F8FAFC',
                          border: '1px solid #E2E8F0',
                          fontSize: '12.5px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                          <span style={{ fontWeight: '700', color: '#0F172A' }}>{perm.userName || 'Link Access'}</span>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: '800',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#E2E8F0',
                              color: '#334155',
                              textTransform: 'uppercase',
                            }}
                          >
                            {perm.role}
                          </span>
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748B', fontStyle: 'italic' }}>
                          {perm.pathText || perm.description}
                        </div>
                      </div>
                    ))}
                    {permissions.length === 0 && (
                      <div style={{ fontSize: '12.5px', color: '#94A3B8', textAlign: 'center', padding: '16px 0' }}>
                        No direct permission overrides. File inherits drive policies.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', margin: 'auto', color: '#94A3B8', padding: '20px' }}>
              <div style={{ fontSize: '42px', marginBottom: '12px' }}>🔍</div>
              <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#475569', margin: '0 0 6px' }}>
                Audit Inspector Standby
              </h4>
              <p style={{ fontSize: '12.5px', color: '#64748B', lineHeight: '1.5', margin: 0 }}>
                Click any cited document button in the chat response to inspect its access pathways, inheritance chains, and security exposure scores.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Ollama Info Modal / Popover ── */}
      {showOllamaInfo && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(11, 19, 43, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '520px',
              width: '90%',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🦙</span> Local Ollama Neural LLM Setup
              </h3>
              <button
                onClick={() => setShowOllamaInfo(false)}
                style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: '1.6', margin: '0 0 16px' }}>
              Your system is currently powered by the <strong>Built-in Native M365 Knowledge Engine</strong> (100% offline, indexing all 57,654 files).
              To optionally run local generative neural models (like Llama 3.2 or Mistral) on this Mac:
            </p>

            <div
              style={{
                background: '#0B132B',
                color: '#00C0FA',
                padding: '14px',
                borderRadius: '10px',
                fontFamily: "'Space Grotesk', monospace",
                fontSize: '12.5px',
                lineHeight: '1.7',
                marginBottom: '16px',
              }}
            >
              <div># 1. Install Ollama (from https://ollama.ai or curl):</div>
              <div style={{ color: '#E2E8F0' }}>curl -fsSL https://ollama.ai/install.sh | sh</div>
              <br />
              <div># 2. Run local Llama 3.2 (air-gapped):</div>
              <div style={{ color: '#05CD99' }}>ollama run llama3.2</div>
            </div>

            <p style={{ fontSize: '12px', color: '#64748B', margin: '0 0 16px' }}>
              Once started on <code>http://localhost:11434</code>, click <strong>"Ping"</strong> in the top HUD and KYRO will automatically switch to your local neural LLM!
            </p>

            <button
              onClick={() => {
                setShowOllamaInfo(false);
                fetchEngineStatus();
              }}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #00C0FA 0%, #0052CC 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '11px',
                fontWeight: '700',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Check Again & Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
